import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import './AdminContactMessages.css';

const AdminContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, read, unread

  useEffect(() => {
    const q = query(collection(db, 'contactMessages'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleMarkAsRead = async (messageId) => {
    try {
      const messageRef = doc(db, 'contactMessages', messageId);
      await updateDoc(messageRef, { isRead: true });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleMarkAsUnread = async (messageId) => {
    try {
      const messageRef = doc(db, 'contactMessages', messageId);
      await updateDoc(messageRef, { isRead: false });
    } catch (error) {
      console.error('Error marking message as unread:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await deleteDoc(doc(db, 'contactMessages', messageId));
        setSelectedMessage(null);
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const filteredMessages = messages.filter(message => {
    if (filter === 'read') return message.isRead;
    if (filter === 'unread') return !message.isRead;
    return true;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="admin-contact-messages">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-contact-messages">
      <div className="page-header">
        <h1>Contact Messages</h1>
        <p>Manage messages sent through the contact form</p>
      </div>

      <div className="messages-container">
        <div className="messages-sidebar">
          <div className="filter-section">
            <h3>Filter Messages</h3>
            <div className="filter-buttons">
              <button 
                className={filter === 'all' ? 'active' : ''} 
                onClick={() => setFilter('all')}
              >
                All ({messages.length})
              </button>
              <button 
                className={filter === 'unread' ? 'active' : ''} 
                onClick={() => setFilter('unread')}
              >
                Unread ({messages.filter(m => !m.isRead).length})
              </button>
              <button 
                className={filter === 'read' ? 'active' : ''} 
                onClick={() => setFilter('read')}
              >
                Read ({messages.filter(m => m.isRead).length})
              </button>
            </div>
          </div>

          <div className="messages-list">
            {filteredMessages.length === 0 ? (
              <div className="no-messages">
                <p>No messages found</p>
              </div>
            ) : (
              filteredMessages.map(message => (
                <div 
                  key={message.id} 
                  className={`message-item ${!message.isRead ? 'unread' : ''} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (!message.isRead) {
                      handleMarkAsRead(message.id);
                    }
                  }}
                >
                  <div className="message-header">
                    <h4>{message.name}</h4>
                    <span className="message-date">{formatDate(message.createdAt)}</span>
                  </div>
                  <p className="message-subject">{message.subject}</p>
                  <p className="message-preview">{message.message.substring(0, 100)}...</p>
                  {!message.isRead && <div className="unread-indicator"></div>}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="message-detail">
          {selectedMessage ? (
            <div className="message-content">
              <div className="message-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => selectedMessage.isRead ? handleMarkAsUnread(selectedMessage.id) : handleMarkAsRead(selectedMessage.id)}
                >
                  {selectedMessage.isRead ? 'Mark as Unread' : 'Mark as Read'}
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => handleDeleteMessage(selectedMessage.id)}
                >
                  Delete Message
                </button>
              </div>

              <div className="message-info">
                <div className="info-row">
                  <label>Name:</label>
                  <span>{selectedMessage.name}</span>
                </div>
                <div className="info-row">
                  <label>Email:</label>
                  <span>
                    <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                  </span>
                </div>
                <div className="info-row">
                  <label>Phone:</label>
                  <span>
                    <a href={`tel:${selectedMessage.phone}`}>{selectedMessage.phone}</a>
                  </span>
                </div>
                <div className="info-row">
                  <label>Subject:</label>
                  <span>{selectedMessage.subject}</span>
                </div>
                <div className="info-row">
                  <label>Date:</label>
                  <span>{formatDate(selectedMessage.createdAt)}</span>
                </div>
                <div className="info-row">
                  <label>Status:</label>
                  <span className={`status ${selectedMessage.isRead ? 'read' : 'unread'}`}>
                    {selectedMessage.isRead ? 'Read' : 'Unread'}
                  </span>
                </div>
              </div>

              <div className="message-body">
                <h3>Message:</h3>
                <div className="message-text">
                  {selectedMessage.message}
                </div>
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <p>Select a message to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContactMessages;

